import { forwardRef, TextareaHTMLAttributes } from 'react';

import { TEXT_INPUT_VARIANTS, TEXT_VARIANTS } from '@pukka/common/constants';
import { tailint } from '@pukka/common/utils';
import { twMerge } from 'tailwind-merge';

const sizes = tailint({
  xl: twMerge(TEXT_VARIANTS['title-1'].size, 'px-4 py-[15px]', 'rounded-md'),
  l: twMerge(TEXT_VARIANTS['subtitle-2'].size, 'px-4 py-[15px]', 'rounded-md'),
  m: twMerge(TEXT_VARIANTS['body-2'].size, 'px-4 py-[15px]', 'rounded-md'),
  s: twMerge(TEXT_VARIANTS['body-1'].size, 'px-4 py-[15px]', 'rounded-md'),
});

const variants = TEXT_INPUT_VARIANTS;

type Variants = Exclude<keyof typeof variants, 'disabled'>;

interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  readonly variant?: Variants;
  readonly size?: keyof typeof sizes;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function Input(
  { variant = 'default', size = 'm', disabled, className, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      disabled={disabled}
      aria-disabled={disabled}
      {...props}
      className={twMerge(
        'resize-none',
        'flex h-28 flex-row',
        'text-neutral-900',
        'focus-ring',
        'w-full border',
        'transition-all duration-300',
        'text-ellipsis',
        'placeholder:text-neutral-400',
        variants[variant],
        sizes[size],
        disabled && variants.disabled,
        className,
      )}
    />
  );
});

export { TextArea };
